import { Injectable } from '@angular/core';
import {
  CanaisRankingModel,
  CancelamentosModel,
  ClientesInativosModel,
  ClientesRankingModel,
  CurvaAbcModel,
  CurvaHorariaModel,
  DescontoAnaliseModel,
  DetalheAtributoModel,
  VendaDiaSemanaModel,
  HeatmapHoraDiaModel,
  MargemRankingModel,
  FormasPagamentoModel,
  DesempenhoVendasEmpresaModel,
  DesempenhoVendasAnoModel,
  GruposProdutosRankingModel,
  ProdutosInativosModel,
  ProdutosRankingModel,
  VendaPeriodoModel,
  TotaisPeriodoModel,
  VendedoresRankingModel,
  LucratividadePeriodoModel,
  VendasKpisPeriodoModel,
} from '@app/data';
import { ApiService } from '@app/gateway';
import type { DashPeriodQueryPayload } from '@app/pages/dashboard/shared';

export type VendaDetalheDimension = 'subgrupo' | 'secao' | 'marca' | 'fabricante';

/**
 * Sales dashboard API. Data source (real or fake) is decided by the backend.
 */
@Injectable({ providedIn: 'root' })
export class VendasService {
  constructor(protected readonly apiService: ApiService) {}

  public async vendaPeriodo(data: DashPeriodQueryPayload): Promise<VendaPeriodoModel[]> {
    try {
      const path: string = `vendas/vendaPeriodo`;
      const result: VendaPeriodoModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async curvaHoraria(data: DashPeriodQueryPayload): Promise<CurvaHorariaModel> {
    try {
      const path: string = `vendas/curvaHoraria`;
      const result: CurvaHorariaModel = await this.apiService.post({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load hourly sales curve.';
      throw new Error(message, { cause: e });
    }
  }

  public async diaSemana(data: DashPeriodQueryPayload): Promise<VendaDiaSemanaModel[]> {
    try {
      const path: string = `vendas/diaSemana`;
      const result: VendaDiaSemanaModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load sales by weekday.';
      throw new Error(message, { cause: e });
    }
  }

  public async heatmapHoraDia(data: DashPeriodQueryPayload): Promise<HeatmapHoraDiaModel> {
    try {
      const path: string = `vendas/heatmapHoraDia`;
      const result: HeatmapHoraDiaModel = await this.apiService.post({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load hour x weekday sales heatmap.';
      throw new Error(message, { cause: e });
    }
  }

  public async descontoAnalise(data: DashPeriodQueryPayload): Promise<DescontoAnaliseModel> {
    try {
      const path: string = `vendas/descontoAnalise`;
      const result: DescontoAnaliseModel = await this.apiService.post({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load discount analysis.';
      throw new Error(message, { cause: e });
    }
  }

  public async cancelamentos(data: DashPeriodQueryPayload): Promise<CancelamentosModel> {
    try {
      const path: string = `vendas/cancelamentos`;
      const result: CancelamentosModel = await this.apiService.post({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load cancellations.';
      throw new Error(message, { cause: e });
    }
  }

  public async formaPagamentoPeriodo(data: DashPeriodQueryPayload): Promise<FormasPagamentoModel[]> {
    try {
      const path: string = `vendas/formaPagamentoPeriodo`;
      const result: FormasPagamentoModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async vendedorRanking(data: DashPeriodQueryPayload): Promise<VendedoresRankingModel[]> {
    try {
      const path: string = `vendas/vendedorRanking`;
      const result: VendedoresRankingModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async canalVendaRanking(data: DashPeriodQueryPayload): Promise<CanaisRankingModel[]> {
    try {
      const path: string = `vendas/canalVendaRanking`;
      const result: CanaisRankingModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async produtosRanking(data: DashPeriodQueryPayload): Promise<ProdutosRankingModel[]> {
    try {
      const path: string = `vendas/produtosRanking`;
      const result: ProdutosRankingModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async grupoProdutosRanking(data: DashPeriodQueryPayload): Promise<GruposProdutosRankingModel[]> {
    try {
      const path: string = `vendas/grupoProdutosRanking`;
      const result: GruposProdutosRankingModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async totaisPeriodo(data: DashPeriodQueryPayload): Promise<TotaisPeriodoModel[]> {
    try {
      const path: string = `vendas/totaisPeriodo`;
      const result: TotaisPeriodoModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async lucratividadePeriodo(data: DashPeriodQueryPayload): Promise<LucratividadePeriodoModel[]> {
    try {
      const path: string = `vendas/lucratividadePeriodo`;
      const result: LucratividadePeriodoModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async produtosInativos(data: Pick<DashPeriodQueryPayload, 'idAccounts'>): Promise<ProdutosInativosModel[]> {
    try {
      const path: string = `vendas/produtosInativos`;
      const result: ProdutosInativosModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async desempenhoVendasAno(data: Pick<DashPeriodQueryPayload, 'idAccounts' | 'status'> & { anos: number[] }): Promise<DesempenhoVendasAnoModel[]> {
    try {
      const path: string = `vendas/desempenhoVendasAno`;
      const result: DesempenhoVendasAnoModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async desempenhoVendasEmpresa(data: Pick<DashPeriodQueryPayload, 'idAccounts' | 'status'> & { ano: number }): Promise<DesempenhoVendasEmpresaModel[]> {
    try {
      const path: string = `vendas/desempenhoVendasEmpresa`;
      const result: DesempenhoVendasEmpresaModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async clientesRanking(data: DashPeriodQueryPayload): Promise<ClientesRankingModel[]> {
    try {
      const path: string = `vendas/clientesRanking`;
      const result: ClientesRankingModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async clientesInativos(data: Pick<DashPeriodQueryPayload, 'idAccounts'>): Promise<ClientesInativosModel[]> {
    try {
      const path: string = `vendas/clientesInativos`;
      const result: ClientesInativosModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async margemRanking(data: DashPeriodQueryPayload): Promise<MargemRankingModel[]> {
    try {
      const path: string = `vendas/margemRanking`;
      const result: MargemRankingModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async curvaABC(data: DashPeriodQueryPayload): Promise<CurvaAbcModel[]> {
    try {
      const path: string = `vendas/curvaABC`;
      const result: CurvaAbcModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async kpisPeriodo(data: DashPeriodQueryPayload): Promise<VendasKpisPeriodoModel[]> {
    try {
      const path: string = `vendas/kpisPeriodo`;
      const result: VendasKpisPeriodoModel[] = await this.apiService.post({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load sales KPIs.';
      throw new Error(message, { cause: e });
    }
  }

  public async detalheVenda(dimension: VendaDetalheDimension, data: DashPeriodQueryPayload): Promise<DetalheAtributoModel[]> {
    try {
      const path: string = `vendas/detalheVenda`;
      const result: DetalheAtributoModel[] = await this.apiService.post({ path, data: { ...data, dimension } });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load sales detail.';
      throw new Error(message, { cause: e });
    }
  }
}
